import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError, api } from '../lib/api.js';
import { useParentLogin, useSession } from '../lib/session.js';
import { FirstRunSetup } from './FirstRunSetup.js';

interface SetupStatus {
  needsSetup: boolean;
  tokenHint: string | null;
}

export function ParentLogin() {
  const navigate = useNavigate();
  const session = useSession();
  const login = useParentLogin();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Is there an account to sign in to yet? A login form shown to somebody who
  // has nothing to sign in with is a dead end, and it was this app's.
  const status = useQuery({
    queryKey: ['setup-status'],
    queryFn: () => api<SetupStatus>('/setup/status'),
    staleTime: 60_000,
  });

  if (session.data?.parent) return <Navigate to="/admin" replace />;

  if (status.data?.needsSetup) {
    return (
      <FirstRunSetup
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['setup-status'] });
          void session.refetch();
        }}
      />
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ email, password }, { onSuccess: () => navigate('/admin', { replace: true }) });
  };

  const message =
    login.error instanceof ApiRequestError
      ? login.error.status === 429
        ? `Too many attempts. Try again in about ${Math.ceil(
            (login.error.body.retryAfterSeconds ?? 60) / 60,
          )} minutes.`
        : login.error.body.message
      : null;

  return (
    <main className="screen">
      <h1>Grown-ups</h1>
      <form className="form" onSubmit={submit}>
        {message && <p className="error-note">{message}</p>}
        <label>
          Email
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button className="btn" type="submit" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
      <button type="button" className="btn btn--quiet" onClick={() => navigate('/')}>
        Back to the quest
      </button>
    </main>
  );
}
